import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StudentPreview — Launcher page.
 * When a School Admin clicks "Preview Student Experience", this page:
 * 1. Requests a demo student token from the backend
 * 2. Backs up the admin's auth token
 * 3. Swaps to the demo token
 * 4. Redirects to the real /dashboard so the admin sees exactly what students see
 */
const StudentPreview = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | error
  const [error, setError] = useState('');

  useEffect(() => {
    const launchPreview = async () => {
      try {
        // Step 1: Get the admin's current token
        const adminToken = apiClient.getToken();
        if (!adminToken) {
          setStatus('error');
          setError('No authentication token found. Please log in again.');
          return;
        }

        // Step 2: Request demo session from backend (using admin auth)
        const res = await fetch(`${API_URL}/api/school/demo-session`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Failed to create demo session');
        }

        const data = await res.json();

        // Step 3: Back up admin token
        const storageType = localStorage.getItem('hibff_auth_token') ? 'local' : 'session';
        sessionStorage.setItem('hibff_admin_token_backup', adminToken);
        sessionStorage.setItem('hibff_admin_storage_type', storageType);

        // Store preview mode metadata
        sessionStorage.setItem(
          'hibff_preview_mode',
          JSON.stringify({
            school_name: data.school_name,
            demo_user_id: data.demo_user_id,
            expires_at: Date.now() + data.expires_in_minutes * 60000,
          })
        );

        // Step 4: Swap to demo token (use sessionStorage so it doesn't persist)
        localStorage.removeItem('hibff_auth_token');
        sessionStorage.setItem('hibff_auth_token', data.token);

        // Step 5: Refresh AuthContext so it picks up the demo student profile
        await refreshUser();

        // Step 6: Navigate to the school student dashboard (duplicate of user dashboard)
        navigate('/school/student', { replace: true });
      } catch (err) {
        console.error('Preview launch failed:', err);
        setStatus('error');
        setError(err.message || 'Something went wrong launching the preview.');
      }
    };

    launchPreview();
  }, [navigate, refreshUser]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="max-w-sm w-full text-center">
          <AlertTriangle size={40} className="mx-auto mb-4" style={{ color: '#F5831F' }} />
          <h2 className="text-lg font-bold mb-2" style={{ color: '#1A1209' }}>
            Preview Unavailable
          </h2>
          <p className="text-sm mb-6" style={{ color: '#888' }}>{error}</p>
          <button
            onClick={() => navigate('/school')}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="back-to-school-btn"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Loading state — shown briefly while token swap happens
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1A1209' }}>
          <Eye size={28} style={{ color: '#F5831F' }} />
        </div>
        <Loader2 className="absolute -bottom-1 -right-1 animate-spin" size={20} style={{ color: '#F5831F' }} />
      </div>
      <div className="text-center">
        <h2 className="text-base font-bold mb-1" style={{ color: '#1A1209' }}>
          Launching Student Experience...
        </h2>
        <p className="text-xs" style={{ color: '#999' }}>
          Setting up your interactive demo session
        </p>
      </div>
    </div>
  );
};

export default StudentPreview;
